import {
  Button, Container, Grid, Stack, TextField, Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { InfoOutlined } from '@mui/icons-material';

import { ConfirmDialogBox } from '../../../components/CustomDialogBox';
import { CustomIcon } from '../../../components/CustomIcon';
import { PackageHeader } from '../../../components/CustomHeading';
import useStyles from '../../../hooks/useStyles';
import { TAXONOMY_TITLES } from '../utils/constants';
import TaxonomyTree from './TaxonomyTree';
import TaxonomyLevels from './TaxonomyLevels';
import FeatureDialog from '../FeatureDialog';
import useTaxonomyTree from '../hooks/useTaxonomyTree';
import styles from '../../../layouts/pages/style';

function TaxonomyBuilder() {
  const { id } = useParams();
  const classes = useStyles(styles)();

  const {
    // state
    loading,
    treeData,
    taxonomyName,
    setExpanded,
    setSelectedNodeId,
    setDirty,
    levels,
    setLevels,
    levelMap,
    expanded,
    dirty,
    openInfo,
    toggleInfo,
    selectedNodeId,
    featureDialogOpen,
    setFeatureDialogOpen,
    dialogSelectedFeatures,
    setDialogSelectedFeatures,
    // function
    handleAction,
    handleSaveFeature,
    handleAddFirstNode,
    handleSaveTaxonomy,
    handleTaxonomyNameChange,
    handleDialogFeatureSave,
  } = useTaxonomyTree();

  return (
    <Container maxWidth="xl">
      <Grid container gap={2}>
        <Grid item xs={12} className={classes.cardStyles}>
          <Stack className={classes.headerStyles}>
            <PackageHeader noPackage heading={TAXONOMY_TITLES.BUILDER} />
            <Stack direction="row" gap={1} alignItems="center">
              <Button
                disabled={!dirty || !taxonomyName.trim() || !treeData.length}
                variant="contained"
                onClick={handleSaveTaxonomy}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12} className={classes.cardStyles}>
          <Typography mb={1} variant="h6">Name</Typography>
          <TextField fullWidth value={taxonomyName} onChange={handleTaxonomyNameChange} />
        </Grid>

        <Grid item xs={12} className={classes.cardStyles}>
          <TaxonomyLevels
            levels={levels}
            setLevels={setLevels}
            treeData={treeData}
            setDirty={setDirty}
            selectedNodeId={selectedNodeId}
          />
        </Grid>

        <Grid item xs={12} className={classes.cardStyles}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="h6">Tree</Typography>
              <CustomIcon size="small" Icon={InfoOutlined} onClick={toggleInfo} />
            </Stack>
            {id === 'new' && !treeData?.length && (
              <Button size="small" variant="outlined" onClick={handleAddFirstNode}>Add First Node</Button>
            )}
          </Stack>

          {!loading && treeData?.length > 0 && (
            <TaxonomyTree
              treeData={treeData}
              selectedNodeId={selectedNodeId}
              setSelectedNodeId={setSelectedNodeId}
              expanded={expanded}
              setExpanded={setExpanded}
              handleAction={handleAction}
              handleSaveFeature={handleSaveFeature}
              levelMap={levelMap}
              depth={1}
              maxDepth={levels.length}
            />
          )}
        </Grid>
      </Grid>

      <ConfirmDialogBox
        openDialog={openInfo}
        title="Taxonomy Tree"
        message="Create and organize nodes to represent categories like function, user roles, or lifecycle stages. You can link each node to features from your existing list to complete the mapping."
        onClose={toggleInfo}
        cancelText="OK"
      />

      <FeatureDialog
        open={featureDialogOpen}
        onClose={() => setFeatureDialogOpen(false)}
        selectedFeatures={dialogSelectedFeatures}
        setSelectedFeatures={setDialogSelectedFeatures}
        onSave={handleDialogFeatureSave}
      />
    </Container>
  );
}

export default TaxonomyBuilder;
